import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { IAttachmentDTO, IAttachmentsFormItemDetails, IAttachmentUpdateDTO } from '..';
import { BehaviorSubject } from 'rxjs';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { asArray, isNull, ComponentPrototype } from '../../../../abstract';
import { AttachmentsFormTypeSelectorComponent } from '../../misc/attachments-form-type-selector.component';
import { tap } from 'rxjs/operators';

const DEFAULT_TITLE = 'Edit';
const MULTIPLE_VALUES_TOKEN = 'VARIOUS';
enum EAttachmentsEditFormKeys {
  TITLE = 'title',
  LABEL = 'label',
  DESCRIPTION = 'description',
}

@Component({
  selector: 'app-attachments-edit-form',
  templateUrl: '../templates/attachments-edit-form.component.html',
  styleUrls: ['../styles/attachments-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AttachmentsEditFormComponent extends ComponentPrototype implements OnInit {
  @Input() public items: IAttachmentDTO | IAttachmentDTO[];

  @Input() public title: string = DEFAULT_TITLE;

  @Output() public submit: EventEmitter<IAttachmentUpdateDTO[]> = new EventEmitter<IAttachmentUpdateDTO[]>();

  @ViewChild('attachmentsEditForm') public attachmentsEditForm: NgbPopover;

  public initialValues: Partial<IAttachmentDTO> = {};

  public updatedValues: Partial<IAttachmentDTO> = {};

  public categorySelector: AttachmentsFormTypeSelectorComponent;

  public isOpened$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public isSaving$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public hasChanges$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public open = (): void => {
    this.attachmentsEditForm?.open();
  };

  public close = (): void => {
    this.attachmentsEditForm?.close();
  };

  public toggleSaving = (state: boolean | null = null): void => {
    this.isSaving$.next(isNull(state) ? !this.isSaving$.value : !!state);
  };

  public submitEditForm = (): void => {
    if (!this.isSaving$.value) {
      const updateQuery: Partial<IAttachmentDTO> = {};

      Object.values(EAttachmentsEditFormKeys).forEach(key => {
        const val = this.updatedValues[key];
        if (val !== MULTIPLE_VALUES_TOKEN) {
          updateQuery[key] = val;
        }
      });

      this.submit.emit(
        asArray(this.items).map(item => {
          const { id } = item;
          return {
            ...updateQuery,
            id,
          };
        })
      );
    }
  };

  public getEditableItems = (): IAttachmentDTO[] => asArray<IAttachmentDTO>(this.items);

  public getDefaultCategory = () => {
    let label;

    this.getEditableItems().forEach(d => {
      if (!label) {
        label = d.label;
      } else {
        label = d.label === label ? label : MULTIPLE_VALUES_TOKEN;
      }
    });

    return label;
  };

  public onEditFormChange = (update: IAttachmentsFormItemDetails): void => {
    Object.keys(update).forEach(key => {
      this.updatedValues[key] = update[key];
    });

    this.hasChanges$.next(
      Object.keys(this.updatedValues).some(key => this.updatedValues[key] !== this.initialValues[key])
    );
  };

  ngOnInit(): void {
    const initialize = () => {
      const initialValues: Partial<IAttachmentDTO> = {};
      asArray(this.items).forEach((item: IAttachmentDTO, i: number) => {
        Object.values(EAttachmentsEditFormKeys).forEach(key => {
          initialValues[key] = i ? (initialValues[key] === item[key] ? item[key] : MULTIPLE_VALUES_TOKEN) : item[key];
        });
      });
      Object.assign(this.initialValues, initialValues);
      Object.assign(this.updatedValues, this.initialValues);
    };

    this.registerSubscriptions(
      this.isOpened$
        .pipe(
          tap(() => this.categorySelector?.clearSelection()),
          tap(() => this.hasChanges$.next(false)),
          tap(state => state && initialize())
        )
        .subscribe()
    );
  }
}
